import { FC, useMemo, useRef, useState, useEffect } from 'react';
import { useAppContext } from '@/contexts';
import { useMasterAdmin } from '@/contexts/masterAdmin';
import { BANNER_HEIGHT } from '@/widgets/ImpersonationBanner/ImpersonationBanner';
import {
  ActionsBox,
  Container,
  HeadBox,
  HeadBtnsBox,
  HeadBtnsLeft,
  HeadBtnsRight,
  MapTabButton,
  MobileFilterBar,
  MobileFilterBarSpacer,
  StickyBar,
  TabViewButton,
  TabViewButtonsBox,
} from './styled';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess'; // unused: chips block disabled
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // unused: chips block disabled
import {
  Box,
  Breadcrumbs,
  // Chip, // unused: chips block disabled
  ClickAwayListener,
  MenuItem,
  Paper,
  Popper,
  Skeleton,
  Typography,
} from '@mui/material';
import Link from 'next/link';
import {
  COLORS,
  MAX_FILTERS_RANGE_VALUES,
  SORT_MODIFICATION_DATE,
  SORTING_OPTIONS,
} from '@/constants';
import { useLocale, useTranslations } from 'next-intl';
import {
  ArrowDown,
  CheckboxGroupMenu,
  // CrossIcon, // unused: chips block disabled
  FilterResetIcon,
  LocationAutocomplete,
  Range,
  TabListIcon,
  TabMapIcon,
  TabTileIcon,
  TextField,
} from '@/components';
import dynamic from 'next/dynamic';
import {
  getActiveFiltersCount,
  // getActiveFilterChips, // unused: chips block disabled
  buildLocationLabel,
  buildSearchTitle,
  isOnlyLand,
} from '@/common';
import {
  Category,
  Equipment,
  FiltersType,
  OfferType,
  OfferTypeObject,
  RangesDimensions,
  ReferenceLocation,
  SearchTabs,
  Sorting,
  UnitsMultilevelList,
} from '@/types';
import type { SelectedMapArea } from '@propileya/geo-widget';

const FilterMapPreview = dynamic(
  () =>
    import('@/components/shared/FilterMapPreview').then((m) => ({ default: m.FilterMapPreview })),
  { ssr: false },
);
import { Filters } from '../Filters';
import { SearchProfile } from '../SearchProfile';

const HOUSING_LIST_SORT_SELECT_ID = 'housing-list-sort-select';
const HOUSING_LIST_SORT_LABEL_ID = `${HOUSING_LIST_SORT_SELECT_ID}-label`;

type HeadProps = {
  data?: { numFound?: number };
  summaryTotal?: number;
  filters: FiltersType;
  mapSelectedAreas?: SelectedMapArea[];
  locations: ReferenceLocation[];
  onLocationsChange: (locations: ReferenceLocation[]) => void;
  unitList?: UnitsMultilevelList;
  tabs: SearchTabs;
  isPending: boolean;
  isFetching?: boolean;
  onChangeValue: (value: Partial<FiltersType>) => void;
  onGoToMap: () => void;
  offerTypeList: OfferTypeObject[];
  chosenOfferType: OfferTypeObject[];
  rangesDimensions: RangesDimensions;
  powerConsumptionClassesList: Category[];
  energyClassFactorsList: Category[];
  onChangeOfferTypeValue: (value: OfferTypeObject[]) => void;
  isLand: boolean;
  onFullResetFilters: () => void;
  canReset?: boolean;
  categoriesMenuList: Category[];
  equipmentMenuList: Equipment[];
  preparedFilters: FiltersType;
  officeName?: string;
  isProject?: boolean;
};

export const Head: FC<HeadProps> = ({
  data,
  summaryTotal,
  filters,
  locations,
  onLocationsChange,
  unitList,
  tabs,
  isPending,
  isFetching = false,
  onChangeValue,
  onGoToMap,
  offerTypeList,
  chosenOfferType,
  rangesDimensions,
  powerConsumptionClassesList,
  energyClassFactorsList,
  onChangeOfferTypeValue,
  isLand,
  onFullResetFilters,
  canReset,
  categoriesMenuList,
  equipmentMenuList,
  preparedFilters,
  officeName,
  isProject = false,
  mapSelectedAreas,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const { setIsStickyBarVisible } = useAppContext();
  const { isImpersonating } = useMasterAdmin();

  const [isSearchProfileVisible, setIsSearchProfileVisible] = useState(false);
  // const [chipsExpanded, setChipsExpanded] = useState(true); // unused: chips block disabled
  const [mapPopoverAnchor, setMapPopoverAnchor] = useState<HTMLElement | null>(null);
  const mapToggleBtnRef = useRef<HTMLElement | null>(null);
  const checkIsSearchProfileVisible = (check: boolean) => setIsSearchProfileVisible(check);

  const isTileFiltersView = tabs === SearchTabs.TILE;

  // Used only for sticky bar logic (not for conditional rendering)
  const isStickyBarActive =
    (!isSearchProfileVisible && tabs !== SearchTabs.LIST && tabs !== SearchTabs.MAP) ||
    getActiveFiltersCount(filters) > 0;

  useEffect(() => {
    setIsStickyBarVisible(isStickyBarActive);
    return () => setIsStickyBarVisible(false);
  }, [isStickyBarActive, setIsStickyBarVisible]);

  const locationTitle = useMemo(() => {
    const isMap = tabs === SearchTabs.MAP;
    const hasMapSelection = !!(filters.mapArea || filters.admunits?.length);

    if (isMap) {
      // When the widget has resolved areas, use them directly — their IDs may differ
      // from the numeric admunit IDs stored in filters.admunits (e.g. after collectAdmunitIds).
      if (mapSelectedAreas?.length) {
        const admunits = mapSelectedAreas.map((a) => ({
          id: a.id,
          name: a.title,
          parent_name: a.parentTitle,
        }));
        return buildLocationLabel({
          admunits,
          andMore: (count) => t('searchResult.andMore', { count }),
        });
      }
      // Widget not yet ready — fall back to saved names (may be empty on first load from URL).
      if (!filters.admunits?.length) return '';
      return buildLocationLabel({
        admunits: filters.admunits,
        andMore: (count) => t('searchResult.andMore', { count }),
      });
    }

    if (hasMapSelection) {
      if (!filters.admunits?.length) return '';
      return buildLocationLabel({
        admunits: filters.admunits,
        andMore: (count) => t('searchResult.andMore', { count }),
      });
    }

    return buildLocationLabel({
      locations: filters.locations,
      unitListValue: filters.unitListValue,
      unitList,
      t,
      andMore: (count) => t('searchResult.andMore', { count }),
    });
  }, [
    tabs,
    filters.mapArea,
    filters.admunits,
    filters.unitListValue,
    filters.locations,
    unitList,
    mapSelectedAreas,
    t,
  ]);

  const activeFiltersCount = useMemo(() => getActiveFiltersCount(filters), [filters]);
  // unused: chips block disabled
  // const activeFilterChips = useMemo(
  //   () => getActiveFilterChips(filters, unitList),
  //   [filters, unitList],
  // );
  // unused: chips block disabled
  // const collapsedMobileFilterChips = useMemo(() => {
  //   const getChipPriority = (id: string) => {
  //     if (id.startsWith('cat-')) return 0;
  //     if (id === 'price' || id === 'rentPrice') return 1;
  //     if (id === 'area') return 2;
  //     if (id === 'rooms') return 3;
  //     if (id.startsWith('unit-')) return 4;
  //     if (id.startsWith('equip-')) return 8;
  //     return 6;
  //   };
  //   return [...activeFilterChips].sort((a, b) => {
  //     const diff = getChipPriority(a.id) - getChipPriority(b.id);
  //     if (diff !== 0) return diff;
  //     return a.label.localeCompare(b.label, 'de');
  //   });
  // }, [activeFilterChips]);

  const isMap = tabs === SearchTabs.MAP;
  const hasMapSelection = !!(filters.mapArea || filters.admunits?.length);

  const handleGoToMap = () => onGoToMap();
  const handleClearMapFilters = () =>
    onChangeValue({ mapArea: undefined, admunits: undefined, currentPage: 0 });
  const mapHasSelection = isMap && (!!mapSelectedAreas?.length || !!filters.mapArea);

  const resolvedNumFound = summaryTotal ?? data?.numFound;
  // On map tab: show item label without count only when there's no selection AND no data yet.
  const numFoundForTitle =
    isMap && !mapHasSelection && resolvedNumFound == null ? null : (resolvedNumFound ?? 0);

  const lastLocationTitleRef = useRef(locationTitle);
  const lastNumFoundRef = useRef(numFoundForTitle);
  // Location label updates immediately (zone names are already known from the widget).
  lastLocationTitleRef.current = locationTitle;
  // numFound updates when loading is done (avoid flashing stale count mid-fetch).
  if (!isPending && !isFetching) {
    lastNumFoundRef.current = numFoundForTitle;
  }
  const displayTitle = buildSearchTitle({
    offerType: filters.offerTypeValue,
    categoryTitles: [],
    locationLabel: lastLocationTitleRef.current,
    translations: {
      isSell: t('searchResult.isSell'),
      isRent: t('searchResult.isRent'),
      in: t('searchResult.in'),
      item: t('searchResult.item'),
      items: t('searchResult.items'),
      projectItem: t('searchResult.projectItem'),
      projectItems: t('searchResult.projectItems'),
    },
    locale,
    numFound: lastNumFoundRef.current,
    isProject,
  });

  return (
    <Container $isMap={isMap}>
      <MobileFilterBar $bannerOffset={isImpersonating ? BANNER_HEIGHT : 0}>
        <Box sx={{ flexShrink: 0 }}>
          <Filters
            useMobileDrawer
            activeFiltersCount={activeFiltersCount}
            numFound={data?.numFound}
            isLand={isLand}
            isMap={tabs === SearchTabs.MAP}
            hasMapSelection={hasMapSelection}
            disabled={isPending}
            canReset={canReset}
            onFullResetFilters={onFullResetFilters}
            onGoToMap={handleGoToMap}
            onClearMapFilters={handleClearMapFilters}
            categories={categoriesMenuList}
            categoriesValue={filters.categoriesValue}
            setCategoriesValue={(categories) =>
              onChangeValue({ categoriesValue: categories, currentPage: 0 })
            }
            equipment={equipmentMenuList}
            equipmentValue={filters.equipmentValue}
            setEquipmentValue={(equipmentList) =>
              onChangeValue({ equipmentValue: equipmentList, currentPage: 0 })
            }
            unitList={unitList}
            unitListValue={filters.unitListValue}
            setUnitListValue={(value) => onChangeValue({ unitListValue: value, currentPage: 0 })}
            rangesDimensions={{
              ...rangesDimensions,
              area: [
                rangesDimensions.area[0],
                isLand ? MAX_FILTERS_RANGE_VALUES.LAND_AREA : MAX_FILTERS_RANGE_VALUES.AREA,
              ],
            }}
            roomNumberValue={filters.roomNumberValue}
            setRoomNumberValue={(value) =>
              onChangeValue({ roomNumberValue: value, currentPage: 0 })
            }
            priceValue={filters.priceValue}
            setPriceValue={(value) => onChangeValue({ priceValue: value, currentPage: 0 })}
            rentPriceValue={filters.rentPriceValue}
            setRentPriceValue={(value) => onChangeValue({ rentPriceValue: value, currentPage: 0 })}
            areaValue={filters.areaValue}
            setAreaValue={(value) => onChangeValue({ areaValue: value, currentPage: 0 })}
            powerConsumptionClassesList={powerConsumptionClassesList}
            powerConsumptionClasses={filters.powerConsumptionClasses}
            setPowerConsumptionClasses={(value) =>
              onChangeValue({ powerConsumptionClasses: value, currentPage: 0 })
            }
            energyClassFactorsList={energyClassFactorsList}
            energyClassFactors={filters.energyClassFactors}
            setEnergyClassFactors={(value) =>
              onChangeValue({ energyClassFactors: value, currentPage: 0 })
            }
            offerTypeList={offerTypeList}
            offerTypeValue={filters.offerTypeValue}
            setOfferTypeValue={onChangeOfferTypeValue}
            locations={locations}
            onLocationsChange={onLocationsChange}
          />
        </Box>
        <Box sx={{ flexShrink: 0, marginLeft: 'auto' }}>
          <SearchProfile
            variant="mobileBar"
            filters={preparedFilters}
            viewType={tabs}
            isLoading={isPending}
            isProject={isProject}
          />
        </Box>
        {tabs !== SearchTabs.MAP && (
          <Box sx={{ minWidth: 0, flex: 1, order: -1 }}>
            <TextField
              id={`${HOUSING_LIST_SORT_SELECT_ID}-mobile`}
              select
              name="sorting"
              value={filters.sorting ?? SORT_MODIFICATION_DATE}
              onChange={(e) =>
                onChangeValue({ sorting: e.target.value as Sorting, currentPage: 0 })
              }
              size="small"
              disabled={isPending}
              fullWidth
              sx={{
                height: '36px',
                width: '100%',
                minWidth: 0,
                '& .MuiInputBase-root': { minWidth: 0, height: '36px', borderRadius: '10px' },
              }}
              slotProps={{
                select: {
                  id: `${HOUSING_LIST_SORT_SELECT_ID}-mobile`,
                  SelectDisplayProps: { id: `${HOUSING_LIST_SORT_SELECT_ID}-mobile` },
                  inputProps: { 'aria-label': t('housingSort.sortBy') },
                  MenuProps: {
                    disableScrollLock: true,
                    PaperProps: {
                      style: {
                        maxHeight: 220,
                        overflowY: 'auto',
                      },
                    },
                  },
                  IconComponent: (props) => <ArrowDown {...props} />,
                },
              }}
            >
              {SORTING_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value === ' ' ? 'sort-none' : option.value}
                  value={option.value}
                >
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </MobileFilterBar>
      <MobileFilterBarSpacer />
      <Breadcrumbs
        aria-label="breadcrumb"
        separator=" / "
        sx={{
          '& .MuiBreadcrumbs-separator': {
            '@media (max-width: 959px)': {
              fontSize: '0px',
              '&::before': { content: '"›"', fontSize: '1.35rem', color: '#C39F8C' },
            },
          },
        }}
      >
        <Link href={'/'}>
          <Typography color={COLORS['primary/600']}>{t('navigation.home')}</Typography>
        </Link>
        <Typography>
          {isProject ? t('navigation.projects') : t('navigation.propertySearch')}
        </Typography>
      </Breadcrumbs>
      <HeadBox>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            minWidth: { xs: undefined, md: '50%' },
          }}
        >
          {officeName && (
            <Typography
              fontSize={'22px'}
              fontWeight={500}
              lineHeight={1.25}
              color={COLORS['primary/500']}
            >
              {`Ergebnis für das Büro: ${officeName}`}
            </Typography>
          )}
          {isFetching ? (
            <Skeleton
              variant="text"
              sx={{
                fontSize: isMap ? { xs: '18px', md: '22px' } : { xs: '20px', md: '24px' },
                lineHeight: 1.25,
                width: { xs: '70%', sm: '55%', md: '420px' },
                maxWidth: '100%',
              }}
            />
          ) : (
            <Typography
              fontSize={isMap ? { xs: '18px', md: '22px' } : { xs: '20px', md: '24px' }}
              fontWeight={600}
              lineHeight={1.25}
              color={COLORS['primary/900']}
              suppressHydrationWarning
            >
              {displayTitle}
            </Typography>
          )}
        </Box>
        <ActionsBox>
          <TabViewButtonsBox>
            <TabViewButton
              onClick={() => onChangeValue({ tabs: SearchTabs.TILE })}
              $isActive={tabs === SearchTabs.TILE}
              variant="outlined"
              suppressHydrationWarning={true}
            >
              <TabListIcon isActive={tabs === SearchTabs.TILE} />
            </TabViewButton>
            <TabViewButton
              onClick={() => onChangeValue({ tabs: SearchTabs.LIST })}
              $isActive={tabs === SearchTabs.LIST}
              variant="outlined"
              suppressHydrationWarning={true}
            >
              <TabTileIcon isActive={tabs === SearchTabs.LIST} />
            </TabViewButton>
          </TabViewButtonsBox>
          {tabs !== SearchTabs.MAP && (
            <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' }, width: '200px' }}>
              <TextField
                id={HOUSING_LIST_SORT_SELECT_ID}
                select
                label={t('housingSort.sortBy')}
                name="sorting"
                value={filters.sorting ?? SORT_MODIFICATION_DATE}
                onChange={(e) =>
                  onChangeValue({ sorting: e.target.value as Sorting, currentPage: 0 })
                }
                size="small"
                disabled={isPending}
                sx={{
                  height: '36px',
                  width: { xs: '100%', md: '200px' },
                  minWidth: 0,
                  '& .MuiInputBase-root': { minWidth: 0, height: '36px' },
                }}
                slotProps={{
                  inputLabel: {
                    component: 'span',
                    id: HOUSING_LIST_SORT_LABEL_ID,
                  },
                  select: {
                    id: HOUSING_LIST_SORT_SELECT_ID,
                    labelId: HOUSING_LIST_SORT_LABEL_ID,
                    SelectDisplayProps: { id: HOUSING_LIST_SORT_SELECT_ID },
                    inputProps: { 'aria-labelledby': HOUSING_LIST_SORT_LABEL_ID },
                    MenuProps: {
                      disableScrollLock: true,
                      PaperProps: {
                        style: {
                          maxHeight: 220,
                          overflowY: 'auto',
                        },
                      },
                    },
                    IconComponent: (props) => <ArrowDown {...props} />,
                  },
                }}
              >
                {SORTING_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value === ' ' ? 'sort-none' : option.value}
                    value={option.value}
                  >
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          <TabViewButton
            onClick={() =>
              onChangeValue({
                tabs: tabs === SearchTabs.TILE ? SearchTabs.LIST : SearchTabs.TILE,
              })
            }
            $isActive={tabs !== SearchTabs.MAP}
            variant="outlined"
            suppressHydrationWarning
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            {tabs === SearchTabs.TILE ? (
              <TabListIcon isActive={true} />
            ) : (
              <TabTileIcon isActive={true} />
            )}
          </TabViewButton>
          <MapTabButton
            type="button"
            onClick={onGoToMap}
            $isActive={tabs === SearchTabs.MAP}
            suppressHydrationWarning
          >
            <TabMapIcon isActive={tabs === SearchTabs.MAP} />
            <Typography
              fontSize={13}
              fontWeight={500}
              color={tabs === SearchTabs.MAP ? COLORS.orange : '#40557D'}
              noWrap
              suppressHydrationWarning
            >
              {t('searchResult.seeOnMap')}
            </Typography>
          </MapTabButton>
        </ActionsBox>
        {/* TODO: active filter chips preview — disabled until mobile layout is fixed
        {activeFilterChips.length > 0 && (
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: chipsExpanded ? 'none' : 80,
                overflow: 'hidden',
              }}
            >
              {(chipsExpanded ? activeFilterChips : collapsedMobileFilterChips).map((chip) => (
                <Chip
                  key={chip.id}
                  icon={
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChangeValue(chip.patch);
                      }}
                      sx={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginRight: 0,
                        '& svg': { width: 16, height: 16 },
                        '& path': { fill: COLORS['neutral/600'] },
                      }}
                    >
                      <CrossIcon />
                    </Box>
                  }
                  label={chip.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    fontSize: 13,
                    height: 32,
                    minWidth: 'unset',
                    maxWidth: 'none',
                    width: 'fit-content',
                    bgcolor: COLORS['neutral/50'],
                    borderColor: COLORS['neutral/300'],
                    color: COLORS['neutral/800'],
                    '& .MuiChip-icon': { marginLeft: '8px', marginRight: '4px' },
                    '& .MuiChip-label': { overflow: 'visible', whiteSpace: 'nowrap' },
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={
                  chipsExpanded ? (
                    <ExpandLessIcon sx={{ color: 'inherit', fontSize: 20 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: 'inherit', fontSize: 20 }} />
                  )
                }
                label={chipsExpanded ? t('searchResult.collapse') : t('searchResult.showAll')}
                size="small"
                onClick={() => setChipsExpanded((prev) => !prev)}
                sx={{
                  bgcolor: COLORS['primary/600'],
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  height: 32,
                  borderRadius: '8px',
                  '& .MuiChip-icon': { color: 'inherit' },
                  '&:hover': { bgcolor: COLORS['primary/700'] },
                }}
              />
              <Chip
                icon={
                  <FilterResetIcon
                    style={{
                      color: COLORS['secondary/700'],
                    }}
                  />
                }
                label={t('searchResult.reset')}
                size="small"
                variant="outlined"
                onClick={() => onFullResetFilters()}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  height: 32,
                  borderRadius: '8px',
                  borderColor: COLORS['secondary/500'],
                  color: COLORS['secondary/700'],
                  '& .MuiChip-icon': { color: COLORS['secondary/700'] },
                  '&:hover': {
                    borderColor: COLORS['secondary/700'],
                    bgcolor: COLORS['secondary/50'],
                  },
                }}
              />
            </Box>
          </Box>
        )}
        */}
      </HeadBox>
      {tabs === SearchTabs.TILE && (
        <HeadBtnsBox>
          <HeadBtnsLeft>
            <>
              {isTileFiltersView && offerTypeList.length > 0 && (
                <CheckboxGroupMenu
                  items={offerTypeList}
                  value={chosenOfferType}
                  onChange={onChangeOfferTypeValue}
                  label={t('searchResult.offerType')}
                  isSelect
                  width="100px"
                  disabled={isPending}
                />
              )}
              {isTileFiltersView &&
                rangesDimensions.roomNumber[0] !== undefined &&
                rangesDimensions.roomNumber[1] !== undefined &&
                !isOnlyLand(filters.categoriesValue) && (
                  <Range
                    label={t('searchResult.rooms')}
                    minValue={rangesDimensions.roomNumber[0] || 0}
                    maxValue={MAX_FILTERS_RANGE_VALUES.ROOMS || 0}
                    value={filters.roomNumberValue}
                    setValue={(value) => onChangeValue({ roomNumberValue: value, currentPage: 0 })}
                    step={1}
                    isSelect
                    isAccordion={false}
                    disabled={isPending}
                  />
                )}
              {isTileFiltersView &&
                rangesDimensions.price[0] !== undefined &&
                rangesDimensions.price[1] !== undefined &&
                filters.offerTypeValue === OfferType.SELL && (
                  <Range
                    isPrice
                    label={t('searchResult.purchasePrice')}
                    minValue={rangesDimensions.price[0] || 0}
                    maxValue={MAX_FILTERS_RANGE_VALUES.SELL_PRICE || 0}
                    value={filters.priceValue}
                    setValue={(value) => onChangeValue({ priceValue: value, currentPage: 0 })}
                    step={1}
                    isSelect
                    isAccordion={false}
                    width="200px"
                    disabled={isPending}
                  />
                )}
              {isTileFiltersView &&
                rangesDimensions.rentPrice[0] !== undefined &&
                rangesDimensions.rentPrice[1] !== undefined &&
                filters.offerTypeValue === OfferType.RENT && (
                  <Range
                    isPrice
                    label={t('searchResult.purchasePrice')}
                    minValue={rangesDimensions.rentPrice[0] || 0}
                    maxValue={MAX_FILTERS_RANGE_VALUES.RENT_PRICE || 0}
                    value={filters.rentPriceValue}
                    setValue={(value) => onChangeValue({ rentPriceValue: value, currentPage: 0 })}
                    step={1}
                    isSelect
                    isAccordion={false}
                    width="200px"
                    disabled={isPending}
                  />
                )}
              {!hasMapSelection && (
                <LocationAutocomplete
                  value={locations}
                  onChange={onLocationsChange}
                  label={t('mainHero.filter.searchPlaceholder')}
                  placeholder={t('searchResult.searchPlaceholder')}
                  sx={{ height: '40px', minWidth: 100 }}
                />
              )}
              {!isProject && hasMapSelection && (
                <>
                  <Box
                    component="button"
                    type="button"
                    ref={mapToggleBtnRef}
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      setMapPopoverAnchor(mapPopoverAnchor ? null : e.currentTarget)
                    }
                    sx={{
                      height: '40px',
                      padding: '0 12px 0 16px',
                      border: `1px solid ${mapPopoverAnchor || hasMapSelection ? COLORS.orange : COLORS['neutral/300']}`,
                      borderRadius: '4px',
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      color: COLORS['secondary/700'],
                      '&:hover': { borderColor: COLORS.orange },
                    }}
                  >
                    <TabMapIcon isActive={hasMapSelection} />
                    <Typography fontSize={13} fontWeight={hasMapSelection ? 600 : 400} noWrap>
                      {t('searchResult.seeOnMap')}
                    </Typography>
                    <ArrowDown
                      style={{
                        transform: mapPopoverAnchor ? 'rotate(180deg)' : undefined,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>
                  <Popper
                    open={Boolean(mapPopoverAnchor)}
                    anchorEl={mapPopoverAnchor}
                    placement="bottom-start"
                    sx={{ zIndex: 1300, mt: '4px !important' }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                  >
                    <ClickAwayListener
                      onClickAway={(e) => {
                        if (mapToggleBtnRef.current?.contains(e.target as Node)) return;
                        setMapPopoverAnchor(null);
                      }}
                    >
                      <Paper
                        elevation={3}
                        sx={{ width: 280, borderRadius: '8px', overflow: 'hidden' }}
                      >
                        <Box
                          onClick={() => {
                            handleGoToMap();
                            setMapPopoverAnchor(null);
                          }}
                          sx={{
                            cursor: 'pointer',
                            padding: '12px 12px 0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                          }}
                        >
                          <FilterMapPreview
                            mapArea={filters.mapArea}
                            admunitIds={filters.admunits?.map((a) => a.id)}
                            height={120}
                          />
                        </Box>
                        <Box
                          sx={{
                            padding: '8px 12px 12px',
                            borderTop: `1px solid ${COLORS['neutral/300']}`,
                            marginTop: '12px',
                          }}
                        >
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => {
                              handleClearMapFilters();
                              setMapPopoverAnchor(null);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: 13,
                              fontWeight: 500,
                              color: COLORS['primary/600'],
                              cursor: 'pointer',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              width: '100%',
                              justifyContent: 'center',
                              visibility: hasMapSelection ? 'visible' : 'hidden',
                              '&:hover': { color: COLORS['primary/700'] },
                            }}
                          >
                            <FilterResetIcon style={{ color: COLORS['primary/600'] }} />
                            {t('searchResult.clearMapSelection')}
                          </Typography>
                        </Box>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </>
              )}
            </>
          </HeadBtnsLeft>

          <HeadBtnsRight>
            <Filters
              activeFiltersCount={activeFiltersCount}
              isLand={isLand}
              isMap={false}
              hasMapSelection={hasMapSelection}
              mapArea={filters.mapArea}
              admunitIds={filters.admunits?.map((a) => a.id)}
              disabled={isPending}
              canReset={canReset}
              onFullResetFilters={onFullResetFilters}
              onGoToMap={handleGoToMap}
              onClearMapFilters={handleClearMapFilters}
              categories={categoriesMenuList}
              categoriesValue={filters.categoriesValue}
              setCategoriesValue={(categories) =>
                onChangeValue({ categoriesValue: categories, currentPage: 0 })
              }
              equipment={equipmentMenuList}
              equipmentValue={filters.equipmentValue}
              setEquipmentValue={(equipmentList) =>
                onChangeValue({ equipmentValue: equipmentList, currentPage: 0 })
              }
              unitList={unitList}
              unitListValue={filters.unitListValue}
              setUnitListValue={(value) => onChangeValue({ unitListValue: value, currentPage: 0 })}
              rangesDimensions={{
                ...rangesDimensions,
                area: [
                  rangesDimensions.area[0],
                  isLand ? MAX_FILTERS_RANGE_VALUES.LAND_AREA : MAX_FILTERS_RANGE_VALUES.AREA,
                ],
              }}
              roomNumberValue={filters.roomNumberValue}
              setRoomNumberValue={(value) =>
                onChangeValue({ roomNumberValue: value, currentPage: 0 })
              }
              priceValue={filters.priceValue}
              setPriceValue={(value) => onChangeValue({ priceValue: value, currentPage: 0 })}
              rentPriceValue={filters.rentPriceValue}
              setRentPriceValue={(value) =>
                onChangeValue({ rentPriceValue: value, currentPage: 0 })
              }
              areaValue={filters.areaValue}
              setAreaValue={(value) => onChangeValue({ areaValue: value, currentPage: 0 })}
              powerConsumptionClassesList={powerConsumptionClassesList}
              powerConsumptionClasses={filters.powerConsumptionClasses}
              setPowerConsumptionClasses={(value) =>
                onChangeValue({ powerConsumptionClasses: value, currentPage: 0 })
              }
              energyClassFactorsList={energyClassFactorsList}
              energyClassFactors={filters.energyClassFactors}
              setEnergyClassFactors={(value) =>
                onChangeValue({ energyClassFactors: value, currentPage: 0 })
              }
              offerTypeList={offerTypeList}
              offerTypeValue={filters.offerTypeValue}
              setOfferTypeValue={onChangeOfferTypeValue}
              locations={locations}
              onLocationsChange={onLocationsChange}
            />

            <SearchProfile
              filters={preparedFilters}
              viewType={tabs}
              isLoading={isPending}
              isProject={isProject}
              elementIsInViewport={checkIsSearchProfileVisible}
            />
          </HeadBtnsRight>
        </HeadBtnsBox>
      )}
      {!isSearchProfileVisible && tabs !== SearchTabs.LIST && tabs !== SearchTabs.MAP && (
        <StickyBar>
          <SearchProfile
            filters={preparedFilters}
            viewType={tabs}
            isLoading={isPending}
            isProject={isProject}
          />
        </StickyBar>
      )}
    </Container>
  );
};
